import { create } from 'zustand';
import { componentsOf, erfuelltRecht } from '@symphenia/shared-types';
import { authApi } from '@/lib/auth.api';
import { setAccessToken, clearAccessToken } from '@/lib/axios';
import { gewaehlteFirma, merkeFirma, vergissFirma } from '@/lib/firma';
import { useThemeStore } from '@/lib/theme.store';
import { resetTerminologyCache } from '@/lib/useTerminology';
import { resetPdfDeliveryCache } from '@/lib/export-delivery';
import {
  setUiSettingsUser,
  syncUiSettingsFromServer,
  hasUiSettingsCache,
  flushUiSettings,
} from '@/lib/ui-settings';
import { istZweiterSchritt, type AuthUser, type LoginCredentials, type LoginResponse, type ZweiterSchritt } from '@/lib/auth.types';
import { verlangtEinrichtung } from '@/lib/zwei-faktor';

type AxiosError = {
  response?: { status?: number; data?: { message?: unknown } };
  code?: string;
};

/**
 * Übersetzt bekannte englische class-validator-Meldungen ins Deutsche.
 * Fallback: Originaltext unverändert zurückgeben.
 */
function translateValidationMessage(msg: string): string {
  const patterns: [RegExp, string][] = [
    [/must be longer than or equal to (\d+) characters?/, 'muss mindestens $1 Zeichen lang sein'],
    [/must be shorter than or equal to (\d+) characters?/, 'darf maximal $1 Zeichen lang sein'],
    [/must be an email/, 'muss eine gültige E-Mail-Adresse sein'],
    [/must be a string/, 'muss eine Zeichenkette sein'],
    [/should not be empty/, 'darf nicht leer sein'],
    [/is not valid/, 'ist ungültig'],
    [/must be a number/, 'muss eine Zahl sein'],
    [/must be a boolean/, 'muss ein Wahrheitswert sein'],
  ];
  for (const [pattern, replacement] of patterns) {
    if (pattern.test(msg)) return msg.replace(pattern, replacement);
  }
  return msg;
}

/**
 * AP-23: Listen-Layouts des Benutzers holen und in den localStorage spiegeln.
 *
 * Beim **ersten** Besuch auf diesem Gerät wird gewartet — sonst rendert eine
 * direkt aufgerufene Listenseite (F5 auf /time-tracking/summary) einmal mit den
 * Code-Defaults, bevor die Antwort da ist. Liegt bereits ein Cache vor, läuft
 * der Abgleich im Hintergrund und hält den Start frei. Fehler sind belanglos:
 * ohne Antwort bleibt der Cache stehen.
 */
async function primeUiSettings(user: AuthUser): Promise<void> {
  // AP-30-D: Nicht die Rolle wandert mit, sondern das **Recht**. Die
  // Standard-Aktionen in den Listen sitzen in einer geteilten Komponente, die
  // den Store nicht anfassen darf (AP-21-B) — sie bekommt deshalb eine Antwort,
  // keine Rollenliste zum Nachrechnen.
  const isTenantAdmin = erfuelltRecht('core.ui_defaults.manage', user.permissions);
  setUiSettingsUser(user.id, isTenantAdmin);
  const sync = syncUiSettingsFromServer(user.id, isTenantAdmin).catch(() => undefined);
  if (!hasUiSettingsCache(user.id)) await sync;
}

/** Wandelt einen Axios-Fehler in eine klare Fehlermeldung um */
function toAuthErrorMessage(err: unknown, fallback: string): string {
  const e = err as AxiosError;

  // Kein Netzwerk / Server nicht erreichbar
  if (!e.response || e.code === 'ERR_NETWORK' || e.code === 'ECONNREFUSED') {
    return 'Server nicht erreichbar. Bitte Netzverbindung prüfen.';
  }

  const status  = e.response.status;
  const raw     = e.response.data?.message;
  const message = Array.isArray(raw) ? raw[0] : raw;

  // Server hat eine konkrete Meldung geliefert → übersetzen und anzeigen
  if (message && typeof message === 'string' && message.length < 200) {
    return translateValidationMessage(message);
  }

  // Ansonsten nach HTTP-Status unterscheiden
  if (status === 401) return fallback;
  if (status === 403) return 'Ihr Konto hat keinen Zugriff auf diese Anwendung.';
  if (status === 429) return 'Zu viele Anmeldeversuche. Bitte kurz warten.';
  if (status && status >= 500) return 'Server-Fehler. Bitte später erneut versuchen.';

  return fallback;
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  /**
   * AP-38-E: `null` — angemeldet. Sonst fehlt noch ein Schritt (zweiter Faktor
   * oder Einrichtung); die Anmeldeseite führt ihn und ruft dann `schliesseAnmeldungAb`.
   */
  login: (credentials: LoginCredentials) => Promise<ZweiterSchritt | null>;
  /** AP-38-E: Die Antwort des letzten Schritts (Code bestätigt, Einrichtung fertig) → angemeldet. */
  schliesseAnmeldungAb: (data: LoginResponse) => Promise<void>;
  logout: () => Promise<void>;
  loadUser: () => Promise<void>;
  clearError: () => void;
  /**
   * AP-11-A: Prüft ob der eingeloggte Tenant ein bestimmtes Modul gebucht hat.
   * Gibt true zurück wenn kein User geladen ist (fail-open für Loading-States).
   */
  hasModule: (module: string) => boolean;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,

  login: async (credentials) => {
    set({ isLoading: true, error: null });
    try {
      // AP-38-B: Arbeitete dieser Tab schon für eine Firma (Sitzung abgelaufen),
      // meldet er sich wieder dort an.
      const firma = credentials.tenantId ?? gewaehlteFirma() ?? undefined;
      const { data } = await authApi.login({ ...credentials, tenantId: firma });
      if (istZweiterSchritt(data)) {
        set({ isLoading: false });
        return data;
      }
      await get().schliesseAnmeldungAb(data);
      return null;
    } catch (err: unknown) {
      set({ isLoading: false, error: toAuthErrorMessage(err, 'E-Mail oder Passwort falsch.') });
      throw err;
    }
  },

  schliesseAnmeldungAb: async (data) => {
    setAccessToken(data.accessToken);
    merkeFirma(data.user.tenantId);
    // Load the full user profile after login
    const meRes = await authApi.me();
    await primeUiSettings(meRes.data);
    set({ user: meRes.data, isAuthenticated: true, isLoading: false, error: null });
  },

  logout: async () => {
    set({ isLoading: true });
    try {
      await authApi.logout();
    } finally {
      clearAccessToken();
      vergissFirma();
      // Mandantengebundene Zwischenspeicher leeren. Sie überleben den
      // Benutzerwechsel sonst und zeigen dem nächsten Mandanten auf demselben
      // Gerät kurz fremde Werte:
      //   Firmenfarbe     → liegt im Theme-Snapshot (localStorage)
      //   Projekt/Auftrag → Modul-Cache in useTerminology
      //   PDF-Ausgabe     → Modul-Cache in export-delivery
      useThemeStore.getState().setTenantAccent(null);
      resetTerminologyCache();
      resetPdfDeliveryCache();
      // AP-23: offene Layout-Änderungen noch senden, dann den Cache vom Benutzer
      // lösen — er selbst bleibt liegen (benutzergebundene Schlüssel).
      flushUiSettings();
      setUiSettingsUser(null);
      set({ user: null, isAuthenticated: false, isLoading: false, error: null });
    }
  },

  loadUser: async () => {
    set({ isLoading: true });
    const { getAccessToken } = await import('@/lib/axios');

    try {
      // If we already have an access token in memory, use it directly
      if (!getAccessToken()) {
        // No access token (e.g. after a page refresh) — attempt a silent refresh
        // using the httpOnly refresh-token cookie that may still be valid
        // AP-38-B: für die Firma dieses Tabs. Beim Start ist noch nichts
        // gezeigt — eine abweichende Antwort wird einfach übernommen.
        const { data: refreshData } = await authApi.refresh(gewaehlteFirma());
        setAccessToken(refreshData.accessToken);
        if (refreshData.tenantId) merkeFirma(refreshData.tenantId);
      }
      const { data } = await authApi.me();
      merkeFirma(data.tenantId);
      await primeUiSettings(data);
      set({ user: data, isAuthenticated: true, isLoading: false });
    } catch (err) {
      // AP-38-E: Nach der Frist verlangt das Erneuern die Einrichtung — dann
      // geht es dorthin statt zur Anmeldung.
      verlangtEinrichtung(err);
      // Refresh failed or user fetch failed — treat as logged out
      clearAccessToken();
      set({ user: null, isAuthenticated: false, isLoading: false });
    }
  },

  clearError: () => set({ error: null }),

  // AP-11-A: Modul-Check — fail-open wenn noch kein User geladen (Initialzustand)
  hasModule: (module: string) => {
    const { user } = get();
    if (!user) return true;
    const aktiv = user.activeModules;
    if (!aktiv) return true;
    if (aktiv.includes(module)) return true;

    // AP-29-N: Eine **Gruppe** steht nie in `activeModules` — gebucht werden
    // Bausteine. Gefragt ist hier aber „darf der Benutzer in die Zeiterfassung",
    // und die Antwort lautet ja, sobald er irgendeinen ihrer Teile hat. Ohne
    // diesen Zweig wäre der ganze Ast `/time-tracking/*` für jeden gesperrt,
    // auch für den, der alles gebucht hat.
    const teile = componentsOf(module);
    return teile.length > 0 && teile.some((t) => aktiv.includes(t.key));
  },
}));
